import React, { useMemo } from 'react';
import {
  BookitoBookingFormValues,
  BookitoEventBookingForm,
  FormAlert,
  QuantityPicker,
  useBookingForm,
} from '@carlito/shared-ui';
import { analyticsEvent } from '@carlito/app-analytics';
import { SUPPORTED_LANGUAGE_CODES } from '@carlito/intl';
import {
  extractApiErrorCodes,
  findOccurrenceForDate,
  LocalizedEventData,
} from '@carlito/bookito-client';
import { useGetHotelConfiguration } from '@carlito/hub-react';
import { Formik } from 'formik';
import EventInfo from './EventInfo';
import FormSubmitButton from './FormSubmitButton';
import { applyTimeOfDate } from '@carlito/date-utils';
import { isEventAllDay } from './helper';
import { useTranslation } from 'react-i18next';

function getUserLanguage() {
  const browserLanguages = [...navigator.languages];
  const selectedLanugage = SUPPORTED_LANGUAGE_CODES.find((availableLanguage) =>
    browserLanguages.find((browserLanguage) => browserLanguage.includes(availableLanguage))
  );

  if (!selectedLanugage) {
    return 'en';
  }

  return selectedLanugage;
}

export type BookingFormProps = {
  event: LocalizedEventData;
  date: Date;
  authId: string;
  availableOnDates: string[];
  shouldPreselectDateInCalendar?: boolean;
};

export function BookingForm({
  event,
  date: onDate,
  authId,
  availableOnDates,
  shouldPreselectDateInCalendar = true,
}: BookingFormProps) {
  const userLanguage = getUserLanguage();

  const { t } = useTranslation();
  const occurrence = findOccurrenceForDate(onDate, event) ?? event?.occurrences[0];
  const startDateTime = applyTimeOfDate(onDate, new Date(occurrence.startDateTime));
  const isAllDay = event ? isEventAllDay(startDateTime, event) : undefined;
  const overallDuration =
    (event?.timeBookability === 'custom'
      ? event?.timeBookabilitySettings?.timeBlockDurationMs
      : occurrence?.durationMs) ?? occurrence.durationMs;

  const initialValues: Partial<BookitoBookingFormValues> = useMemo(() => {
    return {
      bookedForDate: shouldPreselectDateInCalendar ? startDateTime : undefined,
      participants: [
        {
          name: '',
          email: '',
          roomNr: '',
          phoneNr: '',
          isMainBooker: true,
          isExternal: true,
          communicationLanguage: userLanguage,
        },
      ],
    };
  }, [shouldPreselectDateInCalendar, userLanguage]);

  const form = useBookingForm({
    initialEditData: initialValues,
    eventId: event.id,
    hotelId: authId,
    bookingSource: 'web_widget',
  });

  const { state, actions } = form;

  const onSuccessAnimationEnd = () => {
    actions.resetMutation();
    actions.resetCurrentStep();
    actions.resetForm();
  };

  const isTimeSlotEnabled =
    event.timeBookability === 'custom' && !!event.timeBookabilitySettings?.timeBlockDurationMs;

  return (
    <Formik<BookitoBookingFormValues>
      innerRef={state.formikRef}
      initialValues={state.initialValues}
      onSubmit={(values, formikHelpers) => actions.onSubmitBooking(values, formikHelpers)}
    >
      {(props) => {
        const isTouched = Object.values(props.touched).some((t) => t);

        return (
          <form
            className="bookito-form flex mt-10 flex-col min-h-[65vh]"
            onSubmit={props.handleSubmit}
          >
            <EventInfo
              overallDuration={overallDuration}
              isAllDay={isAllDay}
              event={event}
              date={onDate}
              activeLanguage={props.values?.participants?.[0]?.communicationLanguage ?? 'en'}
            />
            <div className="mt-12 flex flex-col">
              <BookitoEventBookingForm
                formStore={form}
                handleSubmit={props.handleSubmit}
                activeLanguage={userLanguage}
                event={event}
                hotelId={authId}
                isTimeSlotEnabled={isTimeSlotEnabled}
                availableOnDates={availableOnDates}
                renderQuantityControls={({
                  count,
                  onCountChange,
                  maxNumberOfAdditionalParticipants,
                }) => (
                  <QuantityPicker
                    maxNumberOfAdditionalParticipants={maxNumberOfAdditionalParticipants}
                    count={count}
                    onCountChange={onCountChange}
                  />
                )}
                renderError={({ fieldError, reset }) => {
                  const error = extractApiErrorCodes(state.error)?.map(t)?.join('\n') || fieldError;
                  return error ? (
                    <FormAlert
                      onClose={() => {
                        reset();
                        actions.resetMutation();
                      }}
                      title={t('error-while-booking')}
                      errors={[error]}
                    />
                  ) : (
                    ''
                  );
                }}
                renderActionButton={({ buttonText, isLoading, isDisabled, onClick }) => (
                  <FormSubmitButton
                    disabled={isDisabled}
                    className={'min-w-[200px]'}
                    onClick={(e) => {
                      e.preventDefault();
                      onClick();
                    }}
                    loading={isLoading}
                  >
                    {buttonText}
                  </FormSubmitButton>
                )}
                renderSubmitButton={({ bookBtnContent, isDisabled }) => {
                  return (
                    <FormSubmitButton
                      onSuccessAnimationEnd={onSuccessAnimationEnd}
                      success={state.isSuccess}
                      loading={state.isLoading}
                      disabled={!isTouched || isDisabled || state.isLoading}
                    >
                      {bookBtnContent}
                    </FormSubmitButton>
                  );
                }}
              />
            </div>
          </form>
        );
      }}
    </Formik>
  );
}

export default BookingForm;
